import { useContext, useMemo } from 'react'
import type { JSX, ReactNode } from 'react'
import { clsx } from 'clsx'
import type { ComponentProps } from '../../types'
import type { FlexContainerAllProps as FlexContainerProps } from '../../../../components/flex/Container'
import WizardContext from '../Context/WizardContext'
import WizardStepContext from './StepContext'
import Flex from '../../../../components/flex/Flex'
import { convertJsxToString } from '../../../../shared/component-helper'
import FieldProvider from '../../Field/Provider'
import type { VisibleWhen } from '../../Form/Visibility'
import withComponentMarkers from '../../../../shared/helpers/withComponentMarkers'

export type WizardStepProps = ComponentProps &
  FlexContainerProps & {
    /**
     * A unique title of the step.
     */
    title?: ReactNode

    /**
     * Will treat the step as non-navigable if set to `true`.
     */
    inactive?: boolean

    /**
     * To determine if the step should be rendered.
     * Used internally by the WizardContainer.
     */
    index?: number

    /**
     * Will make all nested form fields required.
     */
    required?: boolean

    /**
     * If set to `false`, the step will not be rendered.
     */

    include?: boolean
    /**
     * Provide a `path` or `itemPath` together with `hasValue` or `isValid` in order to enable the step. `hasValue` can be a value or a function that returns a boolean. `isValid` uses the validation state of the referenced field.
     */
    includeWhen?: VisibleWhen

    /**
     * Determines if the step should be kept in the DOM. Defaults to `false`.
     */
    keepInDOM?: boolean

    /**
     * If set to `true`, the step will always be rendered.
     * For internal use only.
     */
    prerenderFieldProps?: boolean
  }

function Step(props: WizardStepProps): JSX.Element {
  const {
    id,
    className,
    title,
    index: indexProp,
    inactive,
    include = true,
    includeWhen,
    required,
    keepInDOM: keepInDOMProp,
    prerenderFieldProps,
    children,
    ...restProps
  } = props

  const {
    check,
    enableMapOverChildren,
    activeIndex,
    totalStepsRef,
    stepElementRef,
    stepIndexRef,
    stepsRef,
    keepInDOM,
  } = useContext(WizardContext) || {}

  const wizardStepContext = useContext(WizardStepContext) || {}
  const indexFromContext = wizardStepContext.index

  const totalSteps = totalStepsRef?.current
  const ariaLabel = useMemo(() => convertJsxToString(title), [title])
  const index = useMemo(() => {
    if (indexProp !== undefined) {
      return indexProp
    }

    // Try to use the index from the step context
    if (indexFromContext !== undefined) {
      return indexFromContext
    }

    if (stepsRef?.current) {
      // Try different ways to find the step index
      const { index } =
        Array.from(stepsRef.current.values()).find(
          ({
            id: currentId,
            title: originalTitleProp,
            stringifiedTitle,
          }) => {
            // Try to find the step by id
            if (id !== undefined) {
              return id === currentId
            }

            // Try to find the step by <Translation id="..." />
            const translationId = originalTitleProp?.['props']?.id
            if (translationId) {
              return translationId === title?.['props']?.id
            }

            // Try to find the step by a string (with convertJsxToString) title
            if (stringifiedTitle) {
              return stringifiedTitle === ariaLabel
            }

            // Try to find the step by a JSX title
            return originalTitleProp === title
          }
        ) || {}

      if (index !== undefined) {
        return index
      }
    }

    if (
      totalSteps && // Is only used to make the deps ESLint happy.
      stepIndexRef
    ) {
      stepIndexRef.current += 1
    }

    return stepIndexRef?.current
  }, [
    indexProp,
    stepsRef,
    totalSteps, // "totalSteps" is needed to make dynamic steps work.
    stepIndexRef,
    indexFromContext,
    title,
    id,
    ariaLabel,
  ])

  // If the index is greater than the total steps,
  // it's a sign that e.g. React.StrictMode is used.
  // And if no title or id is given,
  // we need to force a re-render and use an alternative render method,
  // using React.Children.map(children, ...).
  if (!prerenderFieldProps && index >= totalSteps) {
    enableMapOverChildren()
  }

  if (include === false) {
    return null
  }

  if (
    includeWhen &&
    !check({
      visibleWhen: includeWhen,
    })
  ) {
    return null
  }

  const fieldProps =
    typeof required === 'boolean' ? { required } : undefined
  const childrenWithFieldProvider = fieldProps ? (
    <FieldProvider {...fieldProps}>{children}</FieldProvider>
  ) : (
    children
  )

  if (prerenderFieldProps) {
    return (
      <WizardStepContext value={{ index }}>
        {childrenWithFieldProvider as JSX.Element}
      </WizardStepContext>
    )
  }

  const stepRef = activeIndex === index ? stepElementRef : null
  const childrenWithFlex = (
    <WizardStepContext value={{ index }}>
      <Flex.Stack
        className={clsx('dnb-forms-step', className)}
        element="section"
        aria-label={ariaLabel}
        ref={stepRef}
        tabIndex={-1}
        {...restProps}
      >
        {childrenWithFieldProvider}
      </Flex.Stack>
    </WizardStepContext>
  )

  if (
    activeIndex !== index ||
    (includeWhen && !check({ visibleWhen: includeWhen }))
  ) {
    if (keepInDOMProp ?? keepInDOM) {
      return (
        <div title="Wizard Step" hidden>
          {childrenWithFlex}
        </div>
      )
    }

    // Another step is active
    return null
  }

  return childrenWithFlex
}

withComponentMarkers(Step, { _supportsSpacingProps: true })

export default Step
